import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AdminApi } from "../api/client.js";
import type { AdminLocation, AdminLocationInput } from "../api/types.js";
import { Card } from "../components/Card.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Badge } from "../components/Badge.js";
import { US_STATES } from "../components/StatesMultiSelect.js";
import { humanizeErrorMessage } from "../lib/schedule-guards.js";

type Props = { api: AdminApi };

const KIND_OPTIONS = [
  { value: "in_person", label: "In-person" },
  { value: "virtual", label: "Virtual" },
];

// The API requires address_state to match /^[A-Z]{2}$/ — a free-text field
// let staff enter anything (full names, lowercase, extra whitespace) and
// only find out from a raw API error after Save. Reusing US_STATES here
// (already used for provider licensed_states) eliminates that error class
// entirely rather than just reporting it sooner.
const STATE_OPTIONS = US_STATES.map((s) => ({ value: s.code, label: s.name }));

const LocationFormSchema = z
  .object({
    slug: z.string().min(1, "Required"),
    display_name: z.string().min(1, "Required"),
    kind: z.enum(["in_person", "virtual"]),
    address_street1: z.string(),
    address_street2: z.string(),
    address_city: z.string(),
    address_state: z.string(),
    address_zip: z.string(),
    capacity: z.string(),
    default_day_start: z.string().min(1, "Required"),
    default_day_end: z.string().min(1, "Required"),
    active: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (val.kind === "in_person") {
      if (!val.address_street1.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_street1"],
          message: "Required for in-person locations",
        });
      }
      if (!val.address_city.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_city"],
          message: "Required for in-person locations",
        });
      }
      if (!val.address_state.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_state"],
          message: "Required for in-person locations",
        });
      }
      if (!val.address_zip.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address_zip"],
          message: "Required for in-person locations",
        });
      }
      const n = Number(val.capacity);
      if (!val.capacity.trim() || !Number.isFinite(n) || n < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["capacity"],
          message: "Capacity must be at least 1",
        });
      }
    } else if (val.capacity.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["capacity"],
        message: "Virtual locations cannot have a capacity",
      });
    }
  });

type FormValues = z.infer<typeof LocationFormSchema>;

function emptyForm(): FormValues {
  return {
    slug: "",
    display_name: "",
    kind: "in_person",
    address_street1: "",
    address_street2: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    capacity: "",
    default_day_start: "09:00",
    default_day_end: "17:00",
    active: true,
  };
}

function toFormValues(loc: AdminLocation): FormValues {
  return {
    slug: loc.slug,
    display_name: loc.display_name,
    kind: loc.kind,
    address_street1: loc.address_street1 ?? "",
    address_street2: loc.address_street2 ?? "",
    address_city: loc.address_city ?? "",
    address_state: loc.address_state ?? "",
    address_zip: loc.address_zip ?? "",
    capacity: loc.capacity != null ? String(loc.capacity) : "",
    default_day_start: loc.default_day_start,
    default_day_end: loc.default_day_end,
    active: loc.active,
  };
}

function toInput(v: FormValues): AdminLocationInput {
  const isVirtual = v.kind === "virtual";
  return {
    slug: v.slug,
    display_name: v.display_name,
    kind: v.kind,
    address_street1: isVirtual ? null : v.address_street1,
    address_street2: isVirtual ? null : v.address_street2 || null,
    address_city: isVirtual ? null : v.address_city,
    address_state: isVirtual ? null : v.address_state,
    address_zip: isVirtual ? null : v.address_zip,
    capacity: isVirtual ? null : Number(v.capacity),
    default_day_start: v.default_day_start,
    default_day_end: v.default_day_end,
    active: v.active,
  };
}

// Single joined address line for the table row; "—" for virtual locations
// (no address at all) or in-person locations somehow missing every field.
function joinAddress(loc: AdminLocation): string {
  if (loc.kind === "virtual") return "—";
  const line1 = [loc.address_street1, loc.address_street2]
    .filter((p): p is string => !!p && p.trim().length > 0)
    .join(", ");
  const cityStateZip = [
    [loc.address_city, loc.address_state]
      .filter((p): p is string => !!p && p.trim().length > 0)
      .join(", "),
    loc.address_zip,
  ]
    .filter((p): p is string => !!p && p.trim().length > 0)
    .join(" ");
  const joined = [line1, cityStateZip].filter((p) => p.length > 0).join(", ");
  return joined.length > 0 ? joined : "—";
}

export function LocationsPage({ api }: Props) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "locations"],
    queryFn: () => api.listLocations(),
  });
  const [editing, setEditing] = useState<AdminLocation | "new" | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(LocationFormSchema),
    defaultValues: emptyForm(),
  });

  const kind = watch("kind");
  const kindField = register("kind");

  function openNew() {
    reset(emptyForm());
    setEditing("new");
  }

  function openEdit(loc: AdminLocation) {
    reset(toFormValues(loc));
    setEditing(loc);
  }

  function closeForm() {
    setEditing(null);
  }

  const create = useMutation({
    mutationFn: (input: AdminLocationInput) => api.createLocation(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "locations"] });
      setEditing(null);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdminLocationInput }) =>
      api.updateLocation(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "locations"] });
      setEditing(null);
    },
  });

  const submitting = create.isPending || update.isPending;
  const mutationError = create.isError
    ? humanizeErrorMessage((create.error as Error).message)
    : update.isError
      ? humanizeErrorMessage((update.error as Error).message)
      : null;

  function onSubmit(v: FormValues) {
    const input = toInput(v);
    if (editing && editing !== "new") {
      update.mutate({ id: editing.id, input });
    } else {
      create.mutate(input);
    }
  }

  const locations = q.data?.locations ?? [];

  return (
    <div className="grid gap-4">
      <Card
        title="Locations"
        action={
          <button
            type="button"
            onClick={openNew}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            New location
          </button>
        }
      >
        {q.isPending ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : null}
        {q.isError ? (
          <ErrorBanner message="Could not load locations." />
        ) : null}
        {q.data ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Kind</th>
                  <th className="py-2 pr-3">Address</th>
                  <th className="py-2 pr-3">Capacity</th>
                  <th className="py-2 pr-3">Active</th>
                  <th className="py-2 pr-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {locations.map((loc) => (
                  <tr
                    key={loc.id}
                    className="text-sm text-slate-800 hover:bg-slate-50"
                  >
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      {loc.display_name}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge tone="blue">
                        {loc.kind === "virtual" ? "Virtual" : "In-person"}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-slate-700">
                      {joinAddress(loc)}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">
                      {loc.kind === "virtual" ? "—" : (loc.capacity ?? "—")}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge tone={loc.active ? "emerald" : "slate"}>
                        {loc.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        type="button"
                        onClick={() => openEdit(loc)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {editing ? (
        <Card
          title={
            editing === "new" ? "New location" : `Edit ${editing.display_name}`
          }
        >
          {mutationError ? <ErrorBanner message={mutationError} /> : null}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="grid gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Display name"
                required
                {...register("display_name")}
                error={errors.display_name?.message}
              />
              <Field
                label="Slug"
                required
                {...register("slug")}
                error={errors.slug?.message}
              />
            </div>
            <Select
              label="Kind"
              required
              options={KIND_OPTIONS}
              {...kindField}
              onChange={(e) => {
                void kindField.onChange(e);
                // Capacity isn't rendered for virtual locations, but RHF
                // keeps unmounted field state around (shouldUnregister is
                // false by default) — clear it so a stale value/error from
                // a prior in-person selection can't block submission.
                if (e.target.value === "virtual") {
                  setValue("capacity", "");
                  clearErrors("capacity");
                }
              }}
              error={errors.kind?.message}
            />
            {kind === "in_person" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Street address"
                  required
                  {...register("address_street1")}
                  error={errors.address_street1?.message}
                />
                <Field
                  label="Suite / unit"
                  {...register("address_street2")}
                  error={errors.address_street2?.message}
                />
                <Field
                  label="City"
                  required
                  {...register("address_city")}
                  error={errors.address_city?.message}
                />
                <Select
                  label="State"
                  required
                  options={STATE_OPTIONS}
                  placeholder="Select state…"
                  {...register("address_state")}
                  error={errors.address_state?.message}
                />
                <Field
                  label="ZIP"
                  required
                  {...register("address_zip")}
                  error={errors.address_zip?.message}
                />
                <Field
                  label="Capacity"
                  type="number"
                  min={1}
                  required
                  {...register("capacity")}
                  error={errors.capacity?.message}
                />
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Default day start"
                type="time"
                required
                {...register("default_day_start")}
                error={errors.default_day_start?.message}
              />
              <Field
                label="Default day end"
                type="time"
                required
                {...register("default_day_end")}
                error={errors.default_day_end?.message}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                {...register("active")}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              Active
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
